// "Possible Dentally match → Review" badge. Shown on the lead detail hero when the
// async post-create recheck stored `dentally_possible_match` and the lead is not
// linked yet. Opens the existing DentallyPatientMapper in a dialog; never links
// automatically.
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import Iconify from '../Iconify';
import DentallyPatientMapper from './DentallyPatientMapper';
import { pillBadge } from '../../styles/LeadDetailView';
import { TONE_COLOURS } from '../../utils/dentallyPatientLinkStatus';

export default function DentallyPossibleMatchBadge({ practiceId, leadId, candidates, onLinked }) {
  const [open, setOpen] = useState(false);
  if (!candidates?.length) return null;
  const colours = TONE_COLOURS.warning;
  // No names / DOB here on purpose: the stored hint is only {patient id, signals}.
  // The mapper below searches Dentally live, so the reviewer sees details there.
  const tip = `Dentally has ${candidates.length === 1 ? 'a patient' : `${candidates.length} patients`} that may be this person. Review before linking.`;
  return (
    <>
      <Tooltip title={tip} placement='bottom'>
        <Box
          role='button'
          tabIndex={0}
          aria-label='Possible Dentally match. Review'
          data-testid='dentally-possible-match-badge'
          onClick={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
          sx={{ ...pillBadge(colours.bg, colours.fg), cursor: 'pointer' }}
        >
          <Iconify icon='mdi:link-variant-plus' width={16} height={16} />
          Possible Dentally match → Review
        </Box>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Link existing Dentally patient
          <IconButton size='small' onClick={() => setOpen(false)} aria-label='Close'>
            <Iconify icon='eva:close-fill' />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {open && (
            <DentallyPatientMapper
              practiceId={practiceId}
              leadId={leadId}
              // The badge already knows which records were flagged; passing them
              // on is what lets the mapper mark and rank them instead of opening
              // a blank box next to a badge that says a match exists.
              candidates={candidates}
              onPatientMapped={() => { setOpen(false); onLinked?.(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

DentallyPossibleMatchBadge.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  candidates: PropTypes.array,
  onLinked: PropTypes.func,
};
